import { useCallback, useMemo, useState } from 'react'
import { useDismiss } from '../lib/hooks'
import type { ID } from '../lib/types'

export interface PickItem {
  id: ID
  name: string
  /** Extra text the search should match, such as an email. Optional. */
  search?: string
}

/**
 * Pick any number of things from a list.
 *
 * Extracted from PeoplePicker when the Timesheet needed the same control for
 * clients. A second copy would have been ~150 lines that had to stay in step
 * with the first on toggling, dismissal, search and the shape of the label --
 * and this codebase already carries one pair of hand-synchronised twins too
 * many.
 *
 * An empty selection means EVERYTHING, not nothing. That keeps "no filter" and
 * "nothing ticked" from being two states that look identical and behave
 * differently, and it is why clearing closes the popover: it is a terminal
 * choice rather than another toggle.
 *
 * The marks are round, like radio buttons, because that is the look asked for.
 * The SEMANTICS are checkboxes -- role="checkbox" with aria-checked -- so a
 * screen reader still announces that more than one may be chosen, whatever the
 * shape suggests to everyone else.
 */
export function MultiPicker({
  items,
  selected,
  onChange,
  allLabel,
  unit,
  searchPlaceholder,
  ariaLabel,
  emptyText,
  align = 'left',
}: {
  items: readonly PickItem[]
  selected: readonly ID[]
  onChange: (ids: ID[]) => void
  /** Shown on the button, and as the first row, when nothing is selected. */
  allLabel: string
  /** Singular and plural, for the "3 clients" label. */
  unit: readonly [string, string]
  searchPlaceholder: string
  ariaLabel: string
  /** Shown when the list itself is empty, before any search. */
  emptyText: string
  align?: 'left' | 'right'
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')

  const close = useCallback(() => setOpen(false), [])
  const ref = useDismiss<HTMLDivElement>(open, close)

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase()
    return items
      .filter(
        (i) =>
          !query ||
          i.name.toLowerCase().includes(query) ||
          (i.search ?? '').toLowerCase().includes(query),
      )
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [items, q])

  const chosen = new Set(selected)

  const toggle = (id: ID) => {
    const next = new Set(chosen)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange([...next])
  }

  /*
   * Named while a name still fits, then counted. Eight names across a filter
   * bar is noise; "8 clients" is information.
   */
  const label =
    chosen.size === 0
      ? allLabel
      : chosen.size === 1
        ? (items.find((i) => i.id === selected[0])?.name ?? '1 ' + unit[0])
        : chosen.size + ' ' + unit[1]

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        className={chosen.size ? 'picker-btn picker-btn--set' : 'picker-btn'}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => {
          setOpen((v) => !v)
          setQ('')
        }}
      >
        <span
          className="dot"
          style={{ background: chosen.size ? 'var(--ac)' : 'var(--line)' }}
          aria-hidden
        />
        <span className="trunc" title={label}>
          {label}
        </span>
      </button>

      {open && (
        <div className="popover popover--down" style={{ [align]: 0 } as React.CSSProperties}>
          <input
            autoFocus
            className="popover-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />

          <div style={{ padding: 6 }}>
            <button
              type="button"
              className="popover-row"
              onClick={() => {
                onChange([])
                setOpen(false)
              }}
            >
              <Mark on={chosen.size === 0} />
              <span className="trunc" style={{ flex: 1, minWidth: 0 }}>
                {allLabel}
              </span>
            </button>

            {rows.map((i) => (
              <button
                key={i.id}
                type="button"
                role="checkbox"
                aria-checked={chosen.has(i.id)}
                className="popover-row"
                onClick={() => toggle(i.id)}
              >
                <Mark on={chosen.has(i.id)} />
                <span className="trunc" style={{ flex: 1, minWidth: 0 }} title={i.name}>
                  {i.name}
                </span>
              </button>
            ))}

            {rows.length === 0 && (
              <div style={{ padding: '10px 10px 6px', fontSize: 12.5, color: 'var(--mut)' }}>
                {q ? 'Nothing matches that.' : emptyText}
              </div>
            )}

            {chosen.size > 0 && (
              <button
                type="button"
                className="popover-row popover-create"
                onClick={() => onChange([])}
              >
                <span aria-hidden style={{ width: 9 }} />
                <span className="trunc" style={{ flex: 1, minWidth: 0, color: 'var(--mut)' }}>
                  Clear {chosen.size} selected
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Round like a radio, because that is the look asked for; it toggles. */
function Mark({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 13,
        height: 13,
        flexShrink: 0,
        borderRadius: '50%',
        border: '1.5px solid ' + (on ? 'var(--ac)' : 'var(--line)'),
        background: 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {on && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--ac)' }} />}
    </span>
  )
}
